"use client";
import { useRouter } from "next/navigation";
import React, { useEffect, useRef } from "react";

export default function WishList() {
  const router = useRouter();
  // State for storing lists, selected list, and modal/share visibility
  const [lists, setLists] = React.useState(["次の旅行"]);
  const [selectedList, setSelectedList] = React.useState(lists[0]);
  const [showModal, setShowModal] = React.useState(false);
  const [newListName, setNewListName] = React.useState("");
  const [showShare, setShowShare] = React.useState(false);

  // Generate shareable link for the current list
  const shareUrl =
    globalThis.window === undefined
      ? ""
      : `${globalThis.location.origin}${globalThis.location.pathname}?lid=${encodeURIComponent(selectedList)}`;
  const shareRef = useRef<HTMLDivElement | null>(null);

  // Hide share popup when clicking outside
  useEffect(() => {
    if (!showShare) return;
    function handleClickOutside(event: MouseEvent) {
      if (
        shareRef.current &&
        !shareRef.current.contains(event.target as Node)
      ) {
        setShowShare(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showShare]);

  // Create a new wishlist if valid
  const handleCreateList = () => {
    if (newListName.trim() && !lists.includes(newListName.trim())) {
      setLists([...lists, newListName.trim()]);
      setSelectedList(newListName.trim());
      setNewListName("");
      setShowModal(false);
    }
  };

  return (
    <div className="flex min-h-[90vh] flex-col bg-gray-100">
      <div className="mx-auto flex w-full max-w-6xl pt-8 pb-2">
        <button
          className="mr-auto cursor-pointer rounded border border-gray-400 px-4 text-base font-medium text-gray-800 transition hover:bg-gray-200"
          onClick={() => globalThis.history.back()}
        >
          &lt; 戻る
        </button>
      </div>
      <div className="flex flex-col items-center border-b border-gray-200 bg-gray-100 pt-6">
        <div className="relative flex w-full max-w-6xl items-center justify-start gap-6 text-lg font-medium">
          <span className="font-bold">マイリスト：</span>
          <select
            value={selectedList}
            onChange={(e) => setSelectedList(e.target.value)}
            className="ml-2 rounded border border-gray-200 px-3 py-1 text-base font-normal"
          >
            {lists.map((list) => (
              <option key={list} value={list}>
                {list}
              </option>
            ))}
          </select>
          <span className="relative ml-6 flex items-center gap-1 font-normal text-gray-500">
            <button
              className="flex cursor-pointer items-center gap-1 border-none bg-none text-base font-normal text-gray-500 focus:outline-none"
              onClick={() => setShowShare((v) => !v)}
              type="button"
            >
              友達にリストを送信 <span className="text-base">⭮</span>
            </button>
            {showShare && (
              <div
                ref={shareRef}
                className="absolute top-9 left-0 z-10 min-w-[340px] rounded bg-white p-5 shadow-lg"
              >
                <div className="mb-3 text-base text-gray-800">
                  このリンクをコピーして友達に送信すると、リストを共有できます：
                </div>
                <div className="rounded border-2 border-blue-500 bg-blue-50 p-2">
                  <input
                    type="text"
                    value={shareUrl}
                    readOnly
                    className="w-full border-none bg-transparent text-base text-blue-700 outline-none"
                    onFocus={(e) => e.target.select()}
                  />
                </div>
              </div>
            )}
          </span>
          <button
            className="ml-6 flex cursor-pointer items-center gap-1 border-none bg-none text-base font-normal text-gray-500 focus:outline-none"
            onClick={() => setShowModal(true)}
            type="button"
          >
            新しいリストを作成 <span className="text-lg">＋</span>
          </button>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-blue-950/50">
          <div className="relative w-[700px] max-w-[90vw] rounded bg-white p-10 shadow-2xl">
            <div className="mb-6 border-b border-gray-200 pb-2 text-center text-2xl font-bold">
              マイウィッシュリスト：
              <div className="mt-2 text-base font-normal">
                新しいリストの名前を入力してください
              </div>
            </div>
            <div className="mb-6">
              <label
                htmlFor="listName"
                className="text-base font-medium text-gray-800"
              >
                リスト名
              </label>
              <input
                type="text"
                value={newListName}
                onChange={(e) => setNewListName(e.target.value)}
                className="mt-2 block w-full rounded border border-gray-200 bg-gray-50 p-3 text-lg"
                placeholder="リスト名を入力"
              />
            </div>
            <div className="flex justify-end gap-4">
              <button
                className="rounded bg-gray-100 px-8 py-3 text-base font-medium text-gray-800 focus:outline-none"
                onClick={() => {
                  setShowModal(false);
                  setNewListName("");
                }}
                type="button"
              >
                キャンセル
              </button>
              <button
                className="rounded bg-yellow-300 px-8 py-3 text-base font-bold text-gray-800 focus:outline-none disabled:opacity-50"
                onClick={handleCreateList}
                disabled={!newListName.trim()}
                type="button"
              >
                新しいリストを作成
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-1 flex-col items-center justify-center bg-gray-100">
        <div className="mt-12 mb-6">
          <div className="flex h-24 w-24 items-center justify-center rounded-full bg-white shadow">
            <span className="text-5xl text-pink-500">♡</span>
          </div>
        </div>
        <h2 className="mb-3 text-center text-3xl font-bold text-blue-900">
          気に入った施設をリストに保存できます
        </h2>
        <div className="mb-6 max-w-xl text-center text-lg text-gray-500">
          気になる施設を見つけたら、リストに保存して後で簡単に見つけることができます。
        </div>
      </div>

      <div className="bg-primary-950 py-8 text-center">
        <span className="mr-6 text-xl font-medium text-white">
          他のホテルをお探しですか？
        </span>
        <button
          className="cursor-pointer rounded bg-white px-8 py-3 text-base font-bold text-blue-950 shadow focus:outline-none"
          onClick={() => router.push("/hotels")}
        >
          他のホテルを探す
        </button>
      </div>
    </div>
  );
}
